/**
 * Dependency-free constants for the internal tools RTK Query slice.
 *
 * Kept apart from `internalTools.service.ts` so that the global error
 * middleware (`redux/middleware/errorhandler.js`) can import them without
 * pulling the slice (and axios) into the store's import graph. Everything
 * here is pure (no React, no axios).
 */
import { InternalToolKey } from "@callveriq/type/internalTools/Common";

export const INTERNAL_TOOLS_REDUCER_PATH = "internalToolsApiSlice";

export const INTERNAL_TOOLS_API_BASE = "/app/internal_tools/";

/** Backend URL prefix of every tool, under `/app/internal_tools/`. */
export const INTERNAL_TOOL_API_URLS: Record<InternalToolKey, string> = {
    custom_column: `${INTERNAL_TOOLS_API_BASE}custom_column/`,
    subscription: `${INTERNAL_TOOLS_API_BASE}subscription/`,
    transcript: `${INTERNAL_TOOLS_API_BASE}transcript_extraction/`,
    email_reports: `${INTERNAL_TOOLS_API_BASE}email_reports/`,
    gpt_report: `${INTERNAL_TOOLS_API_BASE}gpt_report/`,
    evals: `${INTERNAL_TOOLS_API_BASE}evals/`,
    merge_ops: `${INTERNAL_TOOLS_API_BASE}merge_operations/`,
    domain_context: `${INTERNAL_TOOLS_API_BASE}domain_context/`,
    win_loss: `${INTERNAL_TOOLS_API_BASE}win_loss_report/`,
    tag_creation: `${INTERNAL_TOOLS_API_BASE}tag_creation/`,
    gpt_rerun_file: `${INTERNAL_TOOLS_API_BASE}gpt_rerun/`,
    calibration_rerun: `${INTERNAL_TOOLS_API_BASE}calibration_rerun/`,
    meeting_ai_audit_rerun: `${INTERNAL_TOOLS_API_BASE}meeting_ai_audit_rerun/`,
    meeting_data_packet: `${INTERNAL_TOOLS_API_BASE}meeting_data_packet/`,
    manual_upload: `${INTERNAL_TOOLS_API_BASE}manual_upload/`,
};

export const INTERNAL_TOOLS_ACTIVITY_URL = `${INTERNAL_TOOLS_API_BASE}activity/`;

/** Rows requested by the activity panel when a page does not ask for a specific number. */
export const INTERNAL_TOOLS_ACTIVITY_DEFAULT_LIMIT = 20;

/** Largest `limit` the activity endpoint accepts (it answers 400 above it). */
export const INTERNAL_TOOLS_ACTIVITY_MAX_LIMIT = 200;

/** A usable activity `limit`: an integer between 1 and the backend maximum. */
export const clampActivityLimit = (limit: number | undefined): number => {
    if (limit === undefined || !Number.isFinite(limit)) {
        return INTERNAL_TOOLS_ACTIVITY_DEFAULT_LIMIT;
    }
    return Math.min(
        INTERNAL_TOOLS_ACTIVITY_MAX_LIMIT,
        Math.max(1, Math.trunc(limit))
    );
};

/**
 * Request fields that must never select the tenant or the actor: the tenant
 * is the API Host, the actor is the JWT user. Mirrors the backend's
 * `app.internal_tools.base.CLIENT_TENANT_FIELDS`, which strips them too.
 */
export const INTERNAL_TOOLS_FORBIDDEN_FIELDS = [
    "subdomain",
    "tenant",
    "tenant_name",
    "current_user",
    "requested_by",
] as const;

const FORBIDDEN_FIELD_SET = new Set<string>(INTERNAL_TOOLS_FORBIDDEN_FIELDS);

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (typeof value !== "object" || value === null) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};

const omitForbidden = (value: Record<string, unknown>) =>
    Object.fromEntries(
        Object.entries(value).filter(([key]) => !FORBIDDEN_FIELD_SET.has(key))
    );

const withoutForbiddenQuery = (url: string): string => {
    const queryStart = url.indexOf("?");
    if (queryStart === -1) return url;
    const hashStart = url.indexOf("#", queryStart);
    const query = url.slice(
        queryStart + 1,
        hashStart === -1 ? undefined : hashStart
    );
    const params = new URLSearchParams(query);
    INTERNAL_TOOLS_FORBIDDEN_FIELDS.forEach((key) => params.delete(key));
    const rest = params.toString();
    return `${url.slice(0, queryStart)}${rest ? `?${rest}` : ""}${
        hashStart === -1 ? "" : url.slice(hashStart)
    }`;
};

/**
 * Drops {@link INTERNAL_TOOLS_FORBIDDEN_FIELDS} from the top level of a
 * request's JSON body, `FormData` body, `params` and URL query string. The
 * internal tools base query runs every request through it, so no page can
 * send a client-chosen tenant or actor even by mistake. Other values
 * (strings, Blobs, arrays) pass through untouched.
 */
export const sanitizeInternalToolRequest = <T>(args: T): T => {
    if (typeof args === "string") return withoutForbiddenQuery(args) as T;
    if (typeof args !== "object" || args === null) return args;
    const request = { ...(args as Record<string, unknown>) };
    if (typeof request.url === "string") {
        request.url = withoutForbiddenQuery(request.url);
    }
    const { body, params } = request;
    if (typeof FormData !== "undefined" && body instanceof FormData) {
        const cleaned = new FormData();
        body.forEach((value, key) => {
            if (!FORBIDDEN_FIELD_SET.has(key)) cleaned.append(key, value);
        });
        request.body = cleaned;
    } else if (isPlainObject(body)) {
        request.body = omitForbidden(body);
    }
    if (params instanceof URLSearchParams) {
        const cleaned = new URLSearchParams(params);
        INTERNAL_TOOLS_FORBIDDEN_FIELDS.forEach((key) => cleaned.delete(key));
        request.params = cleaned;
    } else if (isPlainObject(params)) {
        request.params = omitForbidden(params);
    }
    return request as T;
};

export const INTERNAL_TOOL_ACTIVITY_TAG = "InternalToolActivity";

/**
 * One tag per tool plus the shared activity tag. Tools may use `{type, id}`
 * tags of their own type for finer-grained invalidation. Every mutation must
 * also invalidate `InternalToolActivity` so the activity panel refreshes.
 */
export const INTERNAL_TOOL_TAG_TYPES = [
    "CustomColumn",
    "SubscriptionUpdate",
    "TranscriptExtraction",
    "EmailReports",
    "GptReport",
    "Evals",
    "MergeOperations",
    "DomainContext",
    "WinLossReport",
    "TagCreation",
    "GptRerunFile",
    "CalibrationRerun",
    "MeetingAiAuditRerun",
    "MeetingDataPacket",
    "ManualUpload",
    INTERNAL_TOOL_ACTIVITY_TAG,
] as const;

export type InternalToolTagType = (typeof INTERNAL_TOOL_TAG_TYPES)[number];

/**
 * True for every action dispatched by the internal tools slice
 * (`internalToolsApiSlice/executeQuery/*`, `internalToolsApiSlice/executeMutation/*`),
 * including endpoints injected by the per-tool service files.
 */
export const isInternalToolsApiAction = (action: unknown): boolean => {
    const type = (action as { type?: unknown } | null | undefined)?.type;
    return (
        typeof type === "string" &&
        type.startsWith(`${INTERNAL_TOOLS_REDUCER_PATH}/`)
    );
};
